import React, { useState, useMemo, useRef, useEffect, useCallback } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import {
  Box,
  Checkbox,
  Collapse,
  FormControlLabel,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  Popover,
  Button,
  Divider
} from '@mui/material';
import { getCurrentTerms } from '../../../shared/projectTypeUtils';

const BlockTreePopover = ({
  data = [],
  onApply,
  selectedItems = new Set(),
  setSelectedItems,
  disabled = false,
  getTrackerRowCount,
  count
}) => {
  const [expandedBlocks, setExpandedBlocks] = useState(new Set());
  const [expandedSections, setExpandedSections] = useState(new Set());
  const [anchorEl, setAnchorEl] = useState(null);
  const [isApplying, setIsApplying] = useState(false);
  const prevSelectionRef = useRef(new Set());
  const TERMS = getCurrentTerms();

  const isRowDisabled = useCallback((row) => {
    return row && row.scope_assigned === false && row.fully_assigned === true;
  }, []); 

  const isRowChecked = useCallback((row) => {
    return row && (row.scope_assigned === true || row.fully_assigned === true);
  }, []); 

  const isSectionFullyAssigned = useCallback((section) => {
    if (!section || !section.children || !Array.isArray(section.children)) return false;
    const toggleableRows = section.children.filter(row => !isRowDisabled(row));
    if (toggleableRows.length === 0) return false;
    return toggleableRows.every(row => isRowChecked(row));
  }, [isRowDisabled, isRowChecked]);

  const isSectionAssigned = useCallback((section) => {
    if (!section || !section.children || !Array.isArray(section.children)) return false;
    const toggleableRows = section.children.filter(row => !isRowDisabled(row));
    if (toggleableRows.length === 0) return false; // No toggleable rows
    return toggleableRows.every(row => isRowChecked(row));
  }, [isRowDisabled, isRowChecked]);

  const isBlockFullyAssigned = useCallback((block) => {
    if (!block || !block.children || !Array.isArray(block.children)) return false;
    const sectionsWithToggleableRows = block.children.filter(section => {
      const toggleableRows = section.children?.filter(row => !isRowDisabled(row)) || [];
      return toggleableRows.length > 0;
    });
    if (sectionsWithToggleableRows.length === 0) return false; // No toggleable sections
    return sectionsWithToggleableRows.every(section => isSectionFullyAssigned(section));
  }, [isSectionFullyAssigned, isRowDisabled]);

  const isBlockAssigned = useCallback((block) => {
    if (!block || !block.children || !Array.isArray(block.children)) return false;
    const sectionsWithToggleableRows = block.children.filter(section => {
      const toggleableRows = section.children?.filter(row => !isRowDisabled(row)) || [];
      return toggleableRows.length > 0;
    });
    if (sectionsWithToggleableRows.length === 0) return false; 
    return sectionsWithToggleableRows.every(section => isSectionAssigned(section));
  }, [isSectionAssigned, isRowDisabled]);

  const hasInitialized = useRef(false);

  useEffect(() => {
    if (data && Array.isArray(data) && setSelectedItems && !hasInitialized.current) {
      const autoSelectedItems = new Set();
      let hasAnyAssignedData = false;

      data.forEach(block => {
        if (!block || !block.id) return;

        if (block.children && Array.isArray(block.children)) {
          block.children.forEach(section => {
            if (!section || !section.name) return;

            if (section.children && Array.isArray(section.children)) {
              section.children.forEach(row => {
                if (row && row.tracker_row_id) {
                  if (isRowChecked(row) && !isRowDisabled(row)) {
                    autoSelectedItems.add(`row-${row.tracker_row_id}`);
                    hasAnyAssignedData = true;
                  }
                }
              });
            }

            const toggleableRows = section.children?.filter(row => !isRowDisabled(row)) || [];
            if (toggleableRows.length > 0) {
              const allToggleableChecked = toggleableRows.every(row => isRowChecked(row));
              if (allToggleableChecked) {
                autoSelectedItems.add(`section-${block.id}-${section.name}`);
              }
            }
          });
        }

        const sectionsWithToggleableRows = block.children?.filter(section => {
          const toggleableRows = section.children?.filter(row => !isRowDisabled(row)) || [];
          return toggleableRows.length > 0;
        }) || [];

        if (sectionsWithToggleableRows.length > 0) {
          const allValidSectionsSelected = sectionsWithToggleableRows.every(section => {
            const toggleableRows = section.children?.filter(row => !isRowDisabled(row)) || [];
            return toggleableRows.every(row => isRowChecked(row));
          });
          
          if (allValidSectionsSelected) {
            autoSelectedItems.add(`block-${block.id}`);
          }
        }
      });

      if (hasAnyAssignedData && autoSelectedItems.size > 0) {
        setSelectedItems(autoSelectedItems);
        hasInitialized.current = true;
      }
    }
    // eslint-disable-next-line
  }, [data]); 

  useEffect(() => {
    hasInitialized.current = false;
  }, [data]);

  const handleOpenPopover = (event) => {
    if (disabled) return;
    prevSelectionRef.current = new Set(selectedItems || new Set());
    setAnchorEl(event.currentTarget);
  };

  const handleClosePopover = () => {
    setAnchorEl(null);
    setIsApplying(false);
  };

  const handleCancel = () => {
    if (setSelectedItems) {
      setSelectedItems(new Set(prevSelectionRef.current));
    }
    handleClosePopover();
  };

  const handleApply = async () => {
    if (currentSelectedItems.size === 0) {
      return;
    }

    setIsApplying(true);

    try {
      const output = generateOutput();
      await onApply?.(output);
      handleClosePopover();
    } catch (error) {
      console.error('Apply failed:', error);
      setIsApplying(false);
    }
  };

  const open = Boolean(anchorEl);

  const allSelectableItems = useMemo(() => {
    const items = new Set();
    if (!data || !Array.isArray(data)) return items;

    data.forEach(block => {
      if (block && block.id) {
        const hasToggleableContent = block.children?.some(section => 
          section.children?.some(row => !isRowDisabled(row))
        );
        if (hasToggleableContent) {
          items.add(`block-${block.id}`);
        }
        
        if (block.children && Array.isArray(block.children)) {
          block.children.forEach(section => {
            if (section && section.name) {
              const hasToggleableRows = section.children?.some(row => !isRowDisabled(row));
              if (hasToggleableRows) {
                items.add(`section-${block.id}-${section.name}`);
              }
              
              if (section.children && Array.isArray(section.children)) {
                section.children.forEach(row => {
                  if (row && row.tracker_row_id && !isRowDisabled(row)) {
                    items.add(`row-${row.tracker_row_id}`);
                  }
                });
              }
            }
          });
        }
      }
    });
    return items;
  }, [data, isRowDisabled]);

  const currentSelectedItems = selectedItems || new Set();

  const isAllSelected =
    allSelectableItems.size > 0 &&
    Array.from(allSelectableItems).every(item => currentSelectedItems.has(item));

  const isSomeSelected = currentSelectedItems.size > 0 && !isAllSelected;

  const handleSelectAll = event => {
    if (!setSelectedItems) return;

    if (event.target.checked) {
      const allItems = new Set(allSelectableItems);
      data.forEach(block => {
        if (block && block.id) {
          if (isBlockAssigned(block)) {
            allItems.add(`block-${block.id}`);
          }
          if (block.children && Array.isArray(block.children)) {
            block.children.forEach(section => {
              if (section && section.name) {
                if (isSectionAssigned(section)) {
                  allItems.add(`section-${block.id}-${section.name}`);
                }
                if (section.children && Array.isArray(section.children)) {
                  section.children.forEach(row => {
                    if (row && row.tracker_row_id && isRowChecked(row) && !isRowDisabled(row)) {
                      allItems.add(`row-${row.tracker_row_id}`);
                    }
                  });
                }
              }
            });
          }
        }
      });
      setSelectedItems(allItems);
    } else {
      const newSelected = new Set();
      currentSelectedItems.forEach(item => {
        if (item.startsWith('row-')) {
          const rowId = item.replace('row-', '');
          let shouldKeep = false;
          data.forEach(block => {
            block.children?.forEach(section => {
              section.children?.forEach(row => {
                if (row.tracker_row_id === rowId && isRowDisabled(row)) {
                  shouldKeep = true;
                }
              });
            });
          });
          if (shouldKeep) newSelected.add(item);
        }
      });
      setSelectedItems(newSelected);
    }
  };

  const handleBlockToggle = blockId => {
    const newExpanded = new Set(expandedBlocks);
    newExpanded.has(blockId) ? newExpanded.delete(blockId) : newExpanded.add(blockId);
    setExpandedBlocks(newExpanded);
  };

  const handleSectionToggle = sectionKey => {
    const newExpanded = new Set(expandedSections);
    newExpanded.has(sectionKey) ? newExpanded.delete(sectionKey) : newExpanded.add(sectionKey);
    setExpandedSections(newExpanded);
  };

  const handleItemSelect = (itemId, block = null, section = null) => {
    if (!setSelectedItems) return;

    const newSelected = new Set(currentSelectedItems);

    if (newSelected.has(itemId)) {
      newSelected.delete(itemId);
      if (itemId.startsWith('block-') && block) {
        block.children?.forEach(section => {
          const sectionHasToggleableRows = section.children?.some(row => !isRowDisabled(row)) || false;
          if (sectionHasToggleableRows) {
            newSelected.delete(`section-${block.id}-${section.name}`);
            section.children?.forEach(row => {
              if (!isRowDisabled(row)) {
                newSelected.delete(`row-${row.tracker_row_id}`);
              }
            });
          }
        });
      } else if (itemId.startsWith('section-') && section) {
        section.children?.forEach(row => {
          if (!isRowDisabled(row)) {
            newSelected.delete(`row-${row.tracker_row_id}`);
          }
        });
      }
    } else {
      newSelected.add(itemId);
      if (itemId.startsWith('block-') && block) {
        block.children?.forEach(section => {
          const sectionHasToggleableRows = section.children?.some(row => !isRowDisabled(row)) || false;
          if (sectionHasToggleableRows) {
            newSelected.add(`section-${block.id}-${section.name}`);
            section.children?.forEach(row => {
              if (!isRowDisabled(row)) {
                newSelected.add(`row-${row.tracker_row_id}`);
              }
            });
          }
        });
      } else if (itemId.startsWith('section-') && section) {
        section.children?.forEach(row => {
          if (!isRowDisabled(row)) {
            newSelected.add(`row-${row.tracker_row_id}`);
          }
        });
      }
    }

    setSelectedItems(newSelected);
  };

  const isBlockSelected = block => {
    if (currentSelectedItems.has(`block-${block.id}`)) return true;
    
    const allChildrenDisabled = block.children?.every(section => 
      section.children?.every(row => isRowDisabled(row))
    ) || false;
    
    if (allChildrenDisabled && block.children?.length > 0) return true;
    
    const sectionsWithToggleableRows = block.children?.filter(section => {
      const toggleableRows = section.children?.filter(row => !isRowDisabled(row)) || [];
      return toggleableRows.length > 0;
    }) || [];
    
    if (sectionsWithToggleableRows.length === 0) return false;
    
    return sectionsWithToggleableRows.every(section => isSectionSelected(block.id, section));
  };

  const isBlockIndeterminate = block => {
    if (isBlockSelected(block)) return false;
    
    return block.children?.some(section => {
      const toggleableRows = section.children?.filter(row => !isRowDisabled(row)) || [];
      if (toggleableRows.length === 0) return false; // Skip sections with no toggleable rows
      
      return currentSelectedItems.has(`section-${block.id}-${section.name}`) ||
             isSectionSelected(block.id, section) ||
             toggleableRows.some(row => 
               currentSelectedItems.has(`row-${row.tracker_row_id}`)
             );
    }) || false;
  };

  const isSectionSelected = (blockId, section) => {
    if (currentSelectedItems.has(`section-${blockId}-${section.name}`)) return true;
    
    const allChildrenDisabled = section.children?.every(row => isRowDisabled(row)) || false;
    
    if (allChildrenDisabled && section.children?.length > 0) return true;
    
    const toggleableRows = section.children?.filter(row => !isRowDisabled(row)) || [];
    if (toggleableRows.length === 0) return false;
    
    return toggleableRows.every(row => 
      currentSelectedItems.has(`row-${row.tracker_row_id}`)
    );
  };

  const isSectionIndeterminate = (blockId, section) => {
    if (isSectionSelected(blockId, section)) return false;
    
    const toggleableRows = section.children?.filter(row => !isRowDisabled(row)) || [];
    if (toggleableRows.length === 0) return false;
    
    return toggleableRows.some(row =>
      currentSelectedItems.has(`row-${row.tracker_row_id}`)
    );
  };

  const isBlockDisabled = (block) => {
    return block.children?.every(section => 
      section.children?.every(row => isRowDisabled(row))
    ) || false;
  };

  const isSectionDisabled = (section) => {
    return section.children?.every(row => isRowDisabled(row)) || false;
  };

  const isRowSelected = (row) => {
    if (isRowDisabled(row)) {
      return isRowChecked(row);
    }
    
    return currentSelectedItems.has(`row-${row.tracker_row_id}`) || 
           (row.scope_assigned === true);
  };

  const getButtonText = useCallback(() => {
    if (disabled && count) {
      return `${count} Tracker${count !== 1 ? 's' : ''} rows selected`;
    }
  
    if (currentSelectedItems.size === 0) {
      return "Select Block/Section/Tracker";
    }
  
    const selectedCount = getTrackerRowCount(currentSelectedItems);
    return `${selectedCount} tracker${selectedCount !== 1 ? 's' : ''} selected`;

    // eslint-disable-next-line
  }, [count, disabled, currentSelectedItems]);
  

  const handleTextClick = (type, id, event) => {
    event.stopPropagation();
    if (type === 'block') {
      handleBlockToggle(id);
    } else if (type === 'section') {
      handleSectionToggle(id);
    }
  };

  const generateOutput = () => {
    const output = {};
    if (!data || !Array.isArray(data)) return output;

    data.forEach(block => {
      if (!block || !block.id) return;

      const blockId = block.id.toString();
      const sectionOutput = {};
      let allBlockRowsSelected = true;
      let hasAnySelection = false;

      if (block.children && Array.isArray(block.children)) {
        block.children.forEach(section => {
          if (!section || !section.name) return;

          const sectionId = section.name.toString();
          const allRowKeys = section.children?.map(row => `row-${row.tracker_row_id}`) || [];
          const selectedRowKeys = allRowKeys.filter(key => currentSelectedItems.has(key));

          if (selectedRowKeys.length === allRowKeys.length && allRowKeys.length > 0) {
            sectionOutput[sectionId] = -1;
            hasAnySelection = true;
          } else if (selectedRowKeys.length > 0) {
            sectionOutput[sectionId] = section.children
              ?.filter(row => currentSelectedItems.has(`row-${row.tracker_row_id}`))
              .map(row => row.tracker_row_id) || [];
            hasAnySelection = true;
            allBlockRowsSelected = false;
          } else {
            allBlockRowsSelected = false;
          }
        });
      }

      if (hasAnySelection) {
        output[blockId] = allBlockRowsSelected ? -1 : sectionOutput;
      }
    });

    return output;
  };

  return (
    <div>
      <Button
        variant="outlined"
        fullWidth
        sx={{ 
          height: "45px", 
          justifyContent: "flex-start",
          cursor: disabled ? 'not-allowed' : 'pointer',
          borderColor: '#f16253 !important',
          color: '#f16253 !important',
          '&:hover': {
            borderColor: '#f16253 !important',
            backgroundColor: disabled ? 'transparent' : undefined,
            cursor: disabled ? 'not-allowed' : 'pointer',
          },
          '&.Mui-disabled': {
            borderColor: '#f16253 !important',
            color: '#f16253 !important',
            cursor: 'not-allowed',
            pointerEvents: 'auto'
          },
          '& *': {
            cursor: disabled ? 'not-allowed' : 'pointer',
          }
        }}
        onClick={handleOpenPopover}
        disabled={disabled}
      >
        {getButtonText()}
      </Button>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleCancel}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        PaperProps={{ sx: { maxHeight: 400, width: 400, overflow: 'auto' } }}
      >
        <Box sx={{ padding: '4px 16px' }}>
          <FormControlLabel
            control={
              <Checkbox
                checked={isAllSelected}
                indeterminate={isSomeSelected}
                onChange={handleSelectAll}
                size="small"
                disabled={isApplying}
              />
            }
            label={
              <Typography variant="body1" sx={{ fontWeight: 500 }}>
                Select All
              </Typography>
            }
          />
        </Box>

        <List dense sx={{ padding: 0 }}>
          {data.map(block => {
            const blockKey = `block-${block.id}`;
            const isBlockExpanded = expandedBlocks.has(block.id);
            const blockFullyAssigned = isBlockFullyAssigned(block);

            return (
              <Box key={block.id}>
                <ListItem disablePadding>
                  <ListItemIcon sx={{ minWidth: '40px', ml: 1 }}>
                    <Checkbox
                      checked={isBlockSelected(block)}
                      indeterminate={isBlockIndeterminate(block)}
                      onChange={e => {
                        e.stopPropagation();
                        if (!isBlockDisabled(block)) {
                          handleItemSelect(blockKey, block);
                        }
                      }}
                      size="small"
                      disabled={isApplying || isBlockDisabled(block)}
                    />
                  </ListItemIcon>
                  <ListItemButton
                    onClick={(e) => handleTextClick('block', block.id, e)}
                    disabled={isApplying}
                    sx={{ pl: 0 }}
                  >
                    <ListItemText
                      primary={block.name}
                      sx={{
                        '& .MuiListItemText-primary': {
                          color: blockFullyAssigned ? '#666' : 'inherit',
                          fontStyle: 'normal'
                        }
                      }}
                    />
                    {isBlockExpanded ? <ChevronDown size={20} /> : <ChevronRight size={20} />}
                  </ListItemButton>
                </ListItem>

                <Collapse in={isBlockExpanded} timeout={300} unmountOnExit>
                  {block.children?.map(section => {
                    const sectionKey = `section-${block.id}-${section.name}`;
                    const isSectionExpanded = expandedSections.has(sectionKey);

                    return (
                      <Box key={section.name}>
                        <ListItem disablePadding>
                          <ListItemIcon sx={{ minWidth: '40px', ml: 4 }}>
                            <Checkbox
                              checked={isSectionSelected(block.id, section)}
                              indeterminate={isSectionIndeterminate(block.id, section)}
                              onChange={e => {
                                e.stopPropagation();
                                if (!isSectionDisabled(section)) {
                                  handleItemSelect(sectionKey, null, section);
                                }
                              }}
                              size="small"
                              disabled={isApplying || isSectionDisabled(section)}
                            />
                          </ListItemIcon>
                          <ListItemButton
                            onClick={(e) => handleTextClick('section', sectionKey, e)}
                            sx={{ paddingLeft: 0 }}
                            disabled={isApplying}
                          >
                            <ListItemText
                              primary={`${TERMS.SECTION_HEADER} ${section.name}`}
                              sx={{
                                '& .MuiListItemText-primary': {
                                  color: isSectionDisabled(section) ? '#666' : 'inherit',
                                  fontStyle: 'normal'
                                }
                              }}
                            />
                            {isSectionExpanded ? (
                              <ChevronDown size={20} />
                            ) : (
                              <ChevronRight size={20} />
                            )}
                          </ListItemButton>
                        </ListItem>

                        <Collapse in={isSectionExpanded} timeout={300} unmountOnExit>
                          {section.children?.map(row => {
                            const rowKey = `row-${row.tracker_row_id}`;
                            const rowDisabled = isRowDisabled(row);
                            const rowChecked = isRowChecked(row);
 
                            return (
                              <ListItem key={row.tracker_row_id} disablePadding>
                                <ListItemIcon sx={{ minWidth: '40px', ml: 7 }}>
                                  <Checkbox
                                    checked={isRowSelected(row)}
                                    onChange={(e) => {
                                      e.stopPropagation();
                                      if (!rowDisabled) {
                                        handleItemSelect(rowKey);
                                      }
                                    }}
                                    size="small"
                                    disabled={isApplying || rowDisabled}
                                  />
                                </ListItemIcon>
                                <ListItemButton
                                  sx={{ paddingLeft: 0 }}
                                  disabled={isApplying}
                                >
                                  <ListItemText
                                    primary={`${TERMS.ROW_HEADER} ${row.row}`}
                                    sx={{
                                      '& .MuiListItemText-primary': {
                                        color: rowDisabled ? '#666' : (rowChecked && !rowDisabled ? '#f16253' : 'inherit'),
                                        fontStyle: 'normal',
                                        fontWeight: rowChecked && !rowDisabled ? 500 : 'normal'
                                      }
                                    }}
                                  />
                                </ListItemButton>
                              </ListItem>
                            );
                          })}
                        </Collapse>
                      </Box>
                    );
                  })}
                </Collapse>
              </Box>
            );
          })}
        </List>

        <Divider sx={{ mt: 1 }} />

        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            p: 1.5,
            borderTop: '1px solid #e0e0e0',
            position: 'sticky',
            bottom: 0,
            background: '#fff',
            zIndex: 2
          }}
        >
          <Button onClick={handleCancel} disabled={isApplying}>
            Cancel
          </Button>
          <Button
            onClick={handleApply}
            variant="contained"
            disabled={isApplying || currentSelectedItems.size === 0}
          >
            {isApplying ? 'Applying...' : 'Apply'}
          </Button>
        </Box>
      </Popover>
    </div>
  );
};

export default BlockTreePopover;